import type { RouteRecordRaw } from "vue-router";
import { createRouter, createWebHistory } from "vue-router";
import EquipmentRoutes from "./equipment";
import sizingsRoutes from "./sizings";
import quotesRoutes from "./quotes";
import Home from "@/views/Home.vue";
import EquipmentAdmin from "@/views/EquipmentAdmin.vue";
import User from "@/views/User.vue";
import Well from "@/views/Well.vue";
import Wells from "@/views/Wells.vue";
import CacheMiss from "@/views/CacheMiss.vue";
import LoggedOut from "@/views/LoggedOut.vue";
import DeletedSizings from "@/views/DeletedSizings.vue";
import PageNotFound from "@/views/PageNotFound.vue";
import { useLogger } from "@/composables/logger";
import DataDashboard from "@/views/DataAnalytics.vue";

import { navigationGuard, LoginCallback } from "@okta/okta-vue";
import AuthProvider from "@/views/AuthProvider.vue";

// import SpyglassLite from "@/views/SpyglassLite.vue";
import { nextTick } from "vue";
import EulaAdmin from "@/components/EulaAdmin.vue";
import { useOnline } from "@vueuse/core";

const { debug } = useLogger("router");
const isOnline = useOnline();

const routes: Array<RouteRecordRaw> = [
  // {
  //   path: "/lite",
  //   component: SpyglassLite,
  //   name: "Lite"
  // },

  {
    path: "/logout",
    name: "Logout",
    component: LoggedOut
  },
  {
    path: "/",
    component: AuthProvider,
    meta: {
      requiresAuth: true
    },
    children: [
      ...sizingsRoutes,
      ...quotesRoutes,
      {
        path: "/",
        name: "Home",
        component: Home,
        meta: {
          title: "Recent Sizings",
          requiresAuth: true
        }
      },
      {
        path: "/eula",
        name: "Eula",
        component: EulaAdmin,
        meta: {
          requiresAdmin: true,
          requiresAuth: true,
          title: "Eula"
        }
      },
      {
        path: "/admin",
        name: "Admin",
        component: EquipmentAdmin,
        props: route => ({
          routerPath: route.path
        }),
        children: [
          ...EquipmentRoutes,
          {
            path: "deleted-sizings",
            name: "DeletedSizings",
            component: DeletedSizings
          },
          {
            path: "data",
            name: "DataDashboard",
            component: DataDashboard
          }
        ],
        meta: {
          requiresAdmin: true,
          requiresAuth: true,
          title: "Admin"
        }
      },
      // {
      //   path: "/legacy-importer",
      //   name: "Legacy Importer",
      //   component: LegacyImporter,
      //   meta: {
      //     requiresAdmin: true,
      //     title: "Legacy Importer",
      //     requiresAuth: true
      //   }
      // },
      {
        path: "/user",
        name: "User",
        component: User,
        meta: {
          title: "User",
          requiresAuth: true
        }
      },
      {
        path: "/wells/:well",
        name: "Well",
        component: Well,
        props: route => ({
          wellId: route.params.well
        }),
        meta: {
          title: "Well",
          requiresAuth: true
        }
      },
      {
        path: "/wells",
        name: "Wells",
        component: Wells,
        meta: {
          title: "Wells",
          requiresAuth: true
        }
      },
      {
        path: "/cache-miss",
        name: "CacheMiss",
        component: CacheMiss
      },

      {
        path: "/:pathMatch(.*)*",
        name: "NotFound",
        component: PageNotFound,
        meta: {
          title: "404"
        }
      }
    ]
  },
  {
    path: "/login/callback",
    component: LoginCallback
  }
];

function wait(duration: number) {
  return new Promise(resolve => setTimeout(resolve, duration));
}

async function tryScrollToAnchor(hash: string, timeout = 1000, delay = 100) {
  while (timeout > 0) {
    const el = document.querySelector(hash);
    if (el) {
      const { bottom } = el.getBoundingClientRect();
      if (bottom !== 0) {
        el.scrollIntoView({ behavior: "smooth" });
        break;
      }
    }
    await wait(delay);
    timeout = timeout - delay;
  }
}

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition && !to.hash) {
      // push/pop state (forward/back from browser)
      debug(
        "savedPosition found on target route",
        savedPosition,
        "scrolling to it..."
      );
      return savedPosition;
    } else if (to.hash) {
      debug("hash found on target", to.hash, "scrolling to it...");
      tryScrollToAnchor(to.hash, 5000, 50);
    }
  }
});

router.beforeEach(to => {
  // no route guards for Spyglass Lite
  if (to.name === "Lite") {
    return;
  }
  if (to.name === "Eula" && !isOnline.value) {
    router.push({ name: "Home" });
  }
  navigationGuard(to);
});

router.afterEach(to => {
  nextTick(() => {
    const { title } = to.meta;
    document.title = (title ? `${title} - ` : "") + "Spyglass";
  });
});

export default router;
